import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Save, Loader2, X, Pencil, Tag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "./adminApi";

/**
 * Liste des doc_types visibles admin (ordre affiché dans le select).
 * Le backend valide la valeur envoyée : cette liste est purement UI.
 * Correspond à /app/backend/routes/documents.py::ADMIN_DOC_TYPES.
 */
const DOC_TYPE_OPTIONS = [
  { key: "cv", label: "CV" },
  { key: "id_card", label: "Pièce d'identité (unique)" },
  { key: "id_card_recto", label: "CNI / Passeport — recto" },
  { key: "id_card_verso", label: "CNI — verso" },
  { key: "carte_sejour", label: "Carte de séjour (unique)" },
  { key: "carte_sejour_recto", label: "Carte de séjour — recto" },
  { key: "carte_sejour_verso", label: "Carte de séjour — verso" },
  { key: "attestation_secu", label: "Attestation Sécu" },
  { key: "justificatif_domicile", label: "Justificatif de domicile" },
  { key: "certificat_travail", label: "Certificat de travail / bulletins" },
  { key: "visite_medicale", label: "Visite médicale" },
  { key: "rqth", label: "RQTH" },
  { key: "caces_habilitations", label: "CACES / habilitations" },
  { key: "carte_btp", label: "Carte BTP" },
  { key: "test_securite", label: "Tests sécurité (admin uniquement)" },
  { key: "rib", label: "RIB (salarié uniquement)" },
  { key: "other", label: "Autre document" },
];

const labelForDocType = (k) => (DOC_TYPE_OPTIONS.find((o) => o.key === k) || {}).label || k;

/**
 * Dialogue d'édition d'un document existant :
 *  - Renommage (nom de fichier, extension préservée)
 *  - Recatégorisation (changement de doc_type)
 *
 * Props:
 *  - doc: { doc_id, original_filename, doc_type } — contrôle open (open = !!doc)
 *  - onOpenChange(open): fermeture du dialog
 *  - onUpdated(updatedDoc): appelé après succès (rename ET/OU type change)
 *  - showTypeChange: bool (défaut true) — masque le sélecteur de type si false
 */
export default function EditDocumentDialog({ doc, onOpenChange, onUpdated, showTypeChange = true }) {
  const original = doc?.original_filename || "";
  const dotIdx = original.lastIndexOf(".");
  const initialBase = dotIdx > 0 ? original.slice(0, dotIdx) : original;
  const ext = dotIdx > 0 ? original.slice(dotIdx) : "";
  const initialType = doc?.doc_type || "";
  const [name, setName] = useState(initialBase);
  const [docType, setDocType] = useState(initialType);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(dotIdx > 0 ? original.slice(0, dotIdx) : original);
    setDocType(doc?.doc_type || "");
  }, [doc?.doc_id, original, dotIdx, doc?.doc_type]);

  const typeChanged = docType && docType !== initialType;
  const nameChanged = name.trim() && name.trim() !== initialBase;
  const dirty = typeChanged || nameChanged;

  const close = () => onOpenChange?.(false);

  const save = async () => {
    const cleanedName = name.trim();
    if (!cleanedName) { toast.error("Le nom ne peut pas être vide"); return; }
    if (!dirty) { close(); return; }
    setSaving(true);
    let updated = { ...doc };
    try {
      if (nameChanged) {
        const r = await api.patch(`/documents/${doc.doc_id}/rename`, { name: cleanedName });
        updated.original_filename = r.data.original_filename;
      }
      if (typeChanged) {
        const r = await api.patch(`/documents/${doc.doc_id}/type`, { doc_type: docType });
        updated.doc_type = r.data.doc_type;
        // Type change resets validation_status → reflect it locally.
        updated.validation_status = r.data.validation_status || "pending";
        updated.validation_comment = "";
      }
      toast.success(nameChanged && typeChanged ? "Document renommé et recatégorisé" : nameChanged ? "Document renommé" : "Type de document changé");
      onUpdated?.(updated);
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer les changements");
    } finally {
      setSaving(false);
    }
  };

  const currentTypeLabel = useMemo(() => labelForDocType(initialType), [initialType]);

  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md rounded-none" data-testid="edit-doc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#3F3F3F]">
            <Pencil className="w-4 h-4 text-[#662483]" /> Modifier le document
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Renommez le fichier et/ou changez son type si celui-ci a été mal rattaché.
            L&apos;extension du fichier est conservée automatiquement.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 bg-[#FAFAFA] border border-gray-200 p-2">
            <FileText className="w-4 h-4 text-gray-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] text-gray-500 truncate" title={original}>
                Actuel : <strong className="text-[#3F3F3F]">{original}</strong>
              </p>
              <p className="text-[10px] text-gray-400 truncate">
                Type actuel : <span className="text-gray-600">{currentTypeLabel}</span>
              </p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-gray-600 mb-1 block">Nouveau nom</Label>
            <div className="flex items-center gap-1">
              <Input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); else if (e.key === "Escape") close(); }}
                className="rounded-none text-sm flex-1"
                data-testid="edit-doc-name-input"
                placeholder="Nouveau nom du document"
              />
              {ext && <span className="text-xs text-gray-400 select-none px-1" data-testid="edit-doc-ext">{ext}</span>}
            </div>
          </div>

          {showTypeChange && (
            <div>
              <Label className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Type de document
              </Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="rounded-none text-sm h-9" data-testid="edit-doc-type-select">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.key} value={o.key} data-testid={`edit-doc-type-opt-${o.key}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {typeChanged && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 mt-1 px-2 py-1 flex items-start gap-1" data-testid="edit-doc-type-warning">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>Le statut de validation sera remis à « En attente » (le nouveau type peut avoir d&apos;autres critères).</span>
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={close} disabled={saving} className="rounded-none" data-testid="edit-doc-cancel">
              <X className="w-3 h-3 mr-1" /> Annuler
            </Button>
            <Button onClick={save} disabled={saving || !dirty} className="rounded-none bg-[#662483] hover:bg-[#552070] text-white" data-testid="edit-doc-save">
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
